/**
 * One definition of a page's breadcrumb trail, used for BOTH the visible crumbs and the
 * BreadcrumbList in JSON-LD.
 *
 * Before this the two were written out separately on every page, and they had already drifted: the
 * verified-coupons page showed "Home / Coupons / Verified Coupons" but told Google "Home / Verified
 * Coupons", and the comparisons hub showed "Compare Stores" while its schema said "Comparisons".
 * A page now declares the trail once with `breadcrumbTrail(...)` and passes the same array to
 * `<app-breadcrumb>` and to `breadcrumbSchemaFor(...)`, so the two cannot disagree.
 */
import { breadcrumbSchema, Crumb, JsonLd } from './structured-data';

export interface BreadcrumbCrumb {
  /** What the user reads, and the `name` in the schema. */
  label: string;
  /**
   * Router path this crumb links to, e.g. `/categories`. Leave it off the LAST crumb: that is the
   * page you are already on, so it is rendered as plain text with `aria-current="page"` and emitted
   * without an `item` in the schema, which is what Google's spec asks for.
   */
  path?: string;
}

/**
 * A trail with "Home" already on the front — every trail on the site starts there, so putting it in
 * one place is what stops a page from spelling it differently or forgetting it.
 *
 * Pass the rest in order, outermost first:
 *   breadcrumbTrail({ label: 'Stores', path: '/storelist' }, { label: store.name })
 */
export function breadcrumbTrail(...crumbs: BreadcrumbCrumb[]): BreadcrumbCrumb[] {
  return [{ label: 'Home', path: '/' }, ...crumbs];
}

/**
 * The same trail as a BreadcrumbList.
 *
 * `absolute` is the SeoService's own resolver, so the schema carries fully-qualified URLs (a
 * relative `item` is ignored) while the visible crumbs keep the router paths they need.
 */
export function breadcrumbSchemaFor(
  crumbs: BreadcrumbCrumb[],
  absolute: (path: string) => string,
): JsonLd {
  const schemaCrumbs: Crumb[] = crumbs.flatMap((crumb, index) => {
    const isLast = index === crumbs.length - 1;
    // The final crumb is the current page: named, never linked.
    if (isLast) return [{ name: crumb.label }];
    // Google requires an `item` on every crumb but the last ("Missing field item", Search Console,
    // 19 Oct 2026). A middle crumb with no page of its own, such as a product category too small to
    // have a collection page, stays visible as plain text but is left out of the schema.
    return crumb.path ? [{ name: crumb.label, url: absolute(crumb.path) }] : [];
  });

  return breadcrumbSchema(schemaCrumbs);
}
